import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { GraduationCap, Phone, Mail, MapPin, ArrowRight, MessageCircle, Send, Loader2 } from 'lucide-react';
import { CATEGORIES } from '../../data/mock';
import { useSiteLanguage } from '../../context/SiteLanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const { t } = useSiteLanguage();
  const [email, setEmail]               = useState('');
  const [subscribed, setSubscribed]     = useState(false);
  const [subscribing, setSubscribing]   = useState(false);
  const [subscribeError, setSubscribeError] = useState('');

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim() || subscribing) return;
    setSubscribing(true);
    setSubscribeError('');
    try {
      await axios.post(`${API}/enquiries`, {
        first_name: 'Newsletter',
        last_name:  'Subscriber',
        email:      email.trim(),
        message:    'Newsletter subscription request from website footer.',
        type:       'newsletter',
      });
      setSubscribed(true);
      setEmail('');
    } catch {
      setSubscribeError(t('footer.newsletter.error', 'Something went wrong. Please try again.'));
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <footer className="bg-[#1A1735] text-gray-400">

      {/* Newsletter band */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-10 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-white font-bold text-xl">{t('footer.newsletter.title', 'Stay Updated with New Courses')}</h3>
            <p className="text-gray-400 text-sm mt-1">{t('footer.newsletter.desc', 'Get the latest course updates and exclusive offers directly in your inbox.')}</p>
          </div>
          {subscribed ? (
            <div className="bg-white/10 border border-white/20 rounded-xl px-6 py-3 text-white text-sm font-medium flex items-center gap-2">
              <svg className="w-4 h-4 text-[#00B297]" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              {t('footer.newsletter.success', 'Thank you for subscribing!')}
            </div>
          ) : (
            <div className="w-full sm:w-auto">
              <form onSubmit={handleSubscribe} className="flex gap-0 w-full sm:w-auto min-w-[320px] rounded-xl overflow-hidden shadow-sm">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder={t('footer.newsletter.placeholder', 'Enter your email address')}
                  className="flex-1 px-4 py-2.5 bg-white text-gray-800 text-sm focus:outline-none placeholder-gray-400 rounded-l-xl"
                />
                <button
                  type="submit"
                  disabled={subscribing}
                  className="bg-[#DE00A5] hover:bg-[#B80089] disabled:opacity-70 text-white px-4 py-2.5 transition-colors flex items-center gap-1.5 text-sm font-bold rounded-r-xl"
                >
                  {subscribing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  {t('footer.newsletter.btn', 'Subscribe')}
                </button>
              </form>
              {subscribeError && <p className="text-red-300 text-xs mt-2">{subscribeError}</p>}
            </div>
          )}
        </div>
      </div>

      {/* Main footer columns */}
      <div className="max-w-7xl mx-auto px-4 py-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">

        {/* Brand column */}
        <div>
          <Link to="/" className="inline-block mb-5">
            <div className="bg-white rounded-2xl px-4 py-2 inline-block">
              <img
                src="/images/atlas_shield_stacked.png"
                alt="Atlas Academy"
                className="h-20 w-auto object-contain"
              />
            </div>
          </Link>
          <p className="text-sm text-gray-500 leading-relaxed mb-5">
            {t('footer.tagline', 'Empowering learners across the UK with flexible, accredited distance learning programmes. Study at your own pace, achieve your career goals.')}
          </p>
          <a
            href="https://wa.me/447859151818"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-white/8 hover:bg-[#25d366]/15 border border-white/10 rounded-full px-4 py-2 text-xs font-medium text-gray-400 hover:text-white transition-colors"
          >
            <MessageCircle size={13} className="text-[#25d366]" />
            {t('footer.chatWhatsApp', 'Chat with us on WhatsApp')}
          </a>
        </div>

        {/* Course categories */}
        <div>
          <h3 className="text-white font-bold text-sm mb-5 uppercase tracking-widest">{t('footer.courseCategories', 'Course Categories')}</h3>
          <ul className="space-y-2">
            {CATEGORIES.filter(c => c !== 'All Courses').map(cat => (
              <li key={cat}>
                <Link
                  to={`/courses?category=${encodeURIComponent(cat)}`}
                  className="text-sm text-gray-500 hover:text-white transition-colors flex items-center gap-1.5 group"
                >
                  <ArrowRight size={12} className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                  {cat}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Quick links */}
        <div>
          <h3 className="text-white font-bold text-sm mb-5 uppercase tracking-widest">{t('footer.quickLinks', 'Quick Links')}</h3>
          <ul className="space-y-2">
            {[
              { label: t('header.aboutUs',              'About Us'),               href: '/about' },
              { label: t('header.howItWorks',           'How It Works'),           href: '/how-it-works' },
              { label: t('header.universityProgression','University Progression'), href: '/university-progression' },
              { label: t('header.studentSupport',       'Student Support'),        href: '/student-support' },
              { label: t('header.feesPricing',          'Fees & Pricing'),         href: '/pricing' },
              { label: t('header.applyNow',             'Apply Now'),              href: '/apply' },
              { label: 'OTHM Qualifications',                                      href: '/awarding-bodies/othm' },
              { label: 'QUALIFI Qualifications',                                   href: '/awarding-bodies/qualifi' },
              { label: t('footer.contactUs',            'Contact Us'),             href: '/contact' },
              { label: t('footer.privacyPolicy',        'Privacy Policy'),         href: '/privacy-policy' },
              { label: t('footer.termsConditions',      'Terms & Conditions'),     href: '/terms' },
            ].map(item => (
              <li key={item.href + item.label}>
                <Link
                  to={item.href}
                  className="text-sm text-gray-500 hover:text-white transition-colors flex items-center gap-1.5 group"
                >
                  <ArrowRight size={12} className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact info */}
        <div>
          <h3 className="text-white font-bold text-sm mb-5 uppercase tracking-widest">{t('footer.contactUs', 'Contact Us')}</h3>
          <ul className="space-y-3">
            <li className="flex items-start gap-2.5">
              <Phone size={13} className="text-[#4B286D] mt-0.5 shrink-0" />
              <div>
                <a href="tel:+442033555646" dir="ltr" className="text-sm text-gray-500 hover:text-white transition-colors block">+44 (0) 20 3355 5646</a>
                <a href="tel:+447859151818" dir="ltr" className="text-sm text-gray-500 hover:text-white transition-colors block">+44 (0) 78 5915 1818</a>
              </div>
            </li>
            <li className="flex items-center gap-2.5">
              <MessageCircle size={13} className="text-[#25d366] shrink-0" />
              <a href="https://wa.me/447859151818" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-500 hover:text-[#25d366] transition-colors">
                WhatsApp: +44 (0) 78 5915 1818
              </a>
            </li>
            <li className="flex items-center gap-2.5">
              <Mail size={13} className="text-[#4B286D] shrink-0" />
              <a href="mailto:info@atlas-academy.co.uk" className="text-sm text-gray-500 hover:text-white transition-colors">
                info@atlas-academy.co.uk
              </a>
            </li>
            <li className="flex items-start gap-2.5">
              <MapPin size={13} className="text-[#4B286D] mt-0.5 shrink-0" />
              <p className="text-sm text-gray-500">
                Green Building, Unit 1,<br />
                Block M 46, Orchard Place,<br />
                London E14 0JW
              </p>
            </li>
          </ul>

          <div className="mt-5 p-3 bg-white/5 rounded-xl border border-white/10">
            <p className="text-xs text-gray-500 leading-relaxed">
              {t('footer.accreditation', 'Awarding bodies available through Atlas Academy: OTHM & QUALIFI.')}
            </p>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-gray-600">
          <p>{t('footer.rightsReserved', '© {year} Atlas Academy Ltd. All rights reserved.').replace('{year}', new Date().getFullYear())}</p>
          <div className="flex items-center gap-4">
            <Link to="/privacy-policy" className="hover:text-gray-400 transition-colors">{t('footer.privacyPolicy', 'Privacy Policy')}</Link>
            <Link to="/cookie-policy"  className="hover:text-gray-400 transition-colors">{t('footer.cookiePolicy',  'Cookie Policy')}</Link>
            <Link to="/terms"          className="hover:text-gray-400 transition-colors">{t('footer.termsOfUse',   'Terms of Use')}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
